/* this might become a server component in the future. It depends on CategoryTeaser */
'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import { type ReactNode, useRef } from 'react';
import type {
  CategoryTeaserLayoutVariant,
  LinkTarget,
  Positioning,
} from '../../../interfaces/components';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { parseBorderRadius } from '../../utils/borderRadius';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { classes } from '../../utils/classes';
import { getTooltipContent } from '../../utils/getTooltipContent';
import { Decoline } from '../Decoline/Decoline';
import { Icon } from '../Icon/Icon';
import { ImageTeaser } from '../ImageTeaser/ImageTeaser.client';
import { LinkByButton } from '../LinkByButton/LinkByButton.client';
import { LinkByText } from '../LinkByText/LinkByText.client';
import { SoftHyphenInjector } from '../SoftHyphenInjector';
import type { CategoryTeaserItemType } from './types';

const TooltipWrapper = ({
  tooltipContent,
  tooltipPositioning,
  children,
  variant = 'default',
}: {
  tooltipContent?: string;
  tooltipPositioning?: Positioning;
  children: ReactNode;
  variant?: 'default' | 'inline';
}) => (
  <Tooltip variant={variant} content={tooltipContent || undefined} positioning={tooltipPositioning}>
    {children}
  </Tooltip>
);
const LinkWrapper = ({
  finalLink,
  trackClick,
  linkTarget,
  linkRel,
  children,
}: {
  finalLink: string;
  trackClick: () => void;
  linkTarget?: LinkTarget;
  linkRel?: 'nofollow';
  children: ReactNode;
}) => (
  <Link
    href={finalLink}
    onClick={() => trackClick()}
    {...(linkTarget && { target: linkTarget })}
    {...(linkRel && { rel: linkRel })}
  >
    {children}
  </Link>
);

/**
 * The CategoryTeaserItem component displays a teaser for categories with customizable text and layout.
 * It supports various link functionalities including anchor tags, highlighting, and search filters,
 * as well as tracking for analytics purposes. The component can also handle custom identifiers
 * for advanced behavioral tracking or dynamic content systems. It encapsulates an ImageTeaser
 * component for visual representation.
 *
 * @param {CategoryTeaserItemType} data - The data object containing all necessary fields for rendering the teaser,
 * including link information, custom identifier, and any other required teaser data.
 * @returns A React component structured as a flexible box containing the rendered teaser with the appropriate link
 * and custom identifier attributes.
 */
export const CategoryTeaserItem = ({
  data,
  isLazyLoading = false,
  layoutVariant = 'reactangle',
  layoutOutline,
  borderRadius,
  imageSizes,
}: {
  data: CategoryTeaserItemType;
  isLazyLoading?: boolean | null;
  layoutVariant?: CategoryTeaserLayoutVariant;
  layoutOutline: string;
  borderRadius?: string;
  imageSizes?: string;
}) => {
  const {
    // subline
    field_subline_90: subline,

    behavior_paragraph_deco_line: hasDecoline,

    // link
    field_link: link,
    behavior_paragraph_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_tracking: linkTracking,

    // Options: left, center
    behavior_paragraph_alignment: layoutAlignment = 'left',

    // Call-to-action
    behavior_paragraph_cta_icon: ctaIcon,
    behavior_paragraph_cta_size: ctaSize = 'small',
    behavior_paragraph_cta_type_and_alignment: ctaTypeAndAlignment,
    behavior_paragraph_layout: ctaLayout,

    // custom identifier e.G. dynamic yield
    behavior_paragraph_custom_id: customId,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,

    // Glycerin Tracking
    behavior_paragraph_campaign_type: glycerinCampaignType,
    behavior_paragraph_campaignid: glycerinCampaginId,
    behavior_paragraph_category: glycerinCategory,
    behavior_paragraph_detail: glycerinDetail,
    behavior_paragraph_name: glycerinName,

    // promotion item
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
    id,
  } = data;

  const isVariantTypeOnly = layoutVariant === 'type_only';
  const isVariantCircle = layoutVariant === 'circle';

  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;

  const tooltipContent = getTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  const tmpLink: string = link
    ? buildLinkWithArguments(link, {
        field_link_anchor: linkAnchor,
        field_link_highlight: linkHighlight,
        field_link_searchfilter: linkSearchFilter,
        field_tracking: linkTracking,
      })
    : '';
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const teaserRef = useRef<HTMLSpanElement>(null);
  const { trackClick } = useTrackMarketing(data, teaserRef, finalLink);
  const showButton =
    !isVariantTypeOnly &&
    finalLink !== '' &&
    ctaTypeAndAlignment &&
    ['button-below-image', 'button-on-image'].includes(ctaTypeAndAlignment);
  const showTextLink =
    !isVariantTypeOnly &&
    finalLink !== '' &&
    ctaTypeAndAlignment &&
    ['textlink-below-image', 'textlink-on-image'].includes(ctaTypeAndAlignment);
  const ctaAlignment =
    ctaTypeAndAlignment && ['textlink-on-image', 'button-on-image'].includes(ctaTypeAndAlignment)
      ? 'on-image'
      : 'below-image';

  return (
    <div
      className={classes(
        'bg-color-transparent relative flex size-full items-start',
        isVariantTypeOnly ? 'flex-row' : 'flex-col',
        isVariantTypeOnly && 'border-color-grey flex-row flex-wrap content-center border py-1',
      )}
      key={`categoryTeaserItem-${id}`}
      data-dy={customId}
    >
      {!isVariantTypeOnly && (
        <ConditionalWrapper
          condition={!!(tooltipContent && !!tooltipActivation)}
          wrapper={(children) => TooltipWrapper({ tooltipContent, tooltipPositioning, children })}
        >
          <>
            <ImageTeaser
              data={data}
              link={finalLink}
              linkTarget={linkTarget}
              isLazyLoading={isLazyLoading}
              style={{ borderRadius: isVariantCircle ? '50%' : borderRadius }}
              className={classes(
                'size-full',
                layoutOutline !== 'none' && 'border-color-grey border border-solid',
              )}
              sizes={imageSizes}
            />
            {hasDecoline && (
              <Decoline borderRadius={borderRadius ? parseBorderRadius(borderRadius) : undefined} />
            )}
          </>
        </ConditionalWrapper>
      )}
      {subline && (
        <span
          className={classes(
            'text-color-text-dark w-full font-semibold',
            isVariantTypeOnly ? 'text-center' : 'mt-[0.125rem]',
            ctaAlignment === 'on-image' &&
              `absolute bottom-2 ${layoutAlignment === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-2'}`,
          )}
        >
          {showButton && (
            <LinkByButton
              className="mt-1"
              data={{
                type: 'link_by_button',
                field_link: finalLink,
                field_link_target: linkTarget,
                behavior_paragraph_link_rel: linkRel,
                field_text: subline,
                behavior_paragraph_layout: ctaLayout,
                behavior_paragraph_size: ctaSize,
                behavior_paragraph_campaign_type: glycerinCampaignType,
                behavior_paragraph_campaignid: glycerinCampaginId,
                behavior_paragraph_category: glycerinCategory,
                behavior_paragraph_detail: glycerinDetail,
                behavior_paragraph_name: glycerinName,
              }}
              icon={ctaIcon ? 'ArrowRight' : undefined}
            />
          )}

          {showTextLink && (
            <LinkByText
              noUnderline
              className="mt-1"
              data={{
                type: 'link_by_text',
                field_link: finalLink,
                field_link_target: linkTarget,
                behavior_paragraph_link_rel: linkRel,
                field_text: subline,
                field_icon: ctaIcon ? 'ArrowRight' : undefined,
                behavior_paragraph_campaign_type: glycerinCampaignType,
                behavior_paragraph_campaignid: glycerinCampaginId,
                behavior_paragraph_category: glycerinCategory,
                behavior_paragraph_detail: glycerinDetail,
                behavior_paragraph_name: glycerinName,
              }}
            />
          )}

          {!showButton && !showTextLink && (
            <div className="_c_cms_categoryteaser_itemcontent">
              <ConditionalWrapper
                condition={!!link}
                wrapper={(children) =>
                  LinkWrapper({ finalLink, trackClick, linkTarget, linkRel, children })
                }
              >
                <ConditionalWrapper
                  condition={!!(isVariantTypeOnly && tooltipContent && !!tooltipActivation)}
                  wrapper={(chInner) =>
                    TooltipWrapper({
                      variant: 'inline',
                      tooltipContent,
                      tooltipPositioning,
                      children: chInner,
                    })
                  }
                >
                  <div className="flex items-center">
                    <SoftHyphenInjector text={subline} />
                    {ctaIcon && <Icon type="ArrowRight" className="mr-2" />}
                  </div>
                </ConditionalWrapper>
              </ConditionalWrapper>
            </div>
          )}
        </span>
      )}
    </div>
  );
};
